import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useCruises, Cruise } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ArrowLeft, Loader2, CheckCircle, TrendingUp, TrendingDown, FileText, Award, Star, Calculator, Edit3, Edit, ExternalLink, Info, Settings, X, ShieldCheck } from 'lucide-react';
import { CurrencyIcon } from '../components/CurrencyIcon';
import StarRating from '../components/StarRating';
import { motion } from 'framer-motion';
import { parseISO } from 'date-fns';
import { calculateCruiseLoyaltyPoints, formatLoyaltyPoints, getLoyaltyCalculationDescription, isCarnivalCruiseLine, CARNIVAL_PLATINUM_BOOST_STARS } from '../utils/loyaltyUtils';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { PostCruiseDetailsSkeleton } from '../components/LoadingSkeleton';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

interface PostCruiseFormData {
  onboardExpenses?: number;
  gamblingWins?: number;
  gamblingLosses?: number;
  notes?: string;
  loyaltyPointsOverride?: number;
  cabinType?: string;
  ratings?: {
    shipExperience?: number;
    service?: number;
    stateroom?: number;
    foodDining?: number;
    barBeverages?: number;
    entertainment?: number;
  };
}

const PostCruiseDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { updateCruise, getCruise } = useCruises();
  const { getLoyaltyCalculationRules, getLoyaltyTiersForCruiseLine, getLoyaltyTierName, getCruiseLinesInLoyaltyGroup } = useCruiseMetadata();
  const { loyaltyStatus, updateUserLoyaltyStatus } = useAuth();
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useDisplayPreferences();
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [cruise, setCruise] = useState<Cruise | null>(null);
  const [loadingCruise, setLoadingCruise] = useState(true);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<PostCruiseFormData | null>(null);

  // Loyalty-related state
  const [targetLoyaltyProgram, setTargetLoyaltyProgram] = useState<string>('');
  const [availableLoyaltyPrograms, setAvailableLoyaltyPrograms] = useState<import('../context/CruiseMetadataContext').CruiseMetadata[]>([]);
  const [currentLoyaltyPoints, setCurrentLoyaltyPoints] = useState<number>(0);
  const [projectedLoyaltyPoints, setProjectedLoyaltyPoints] = useState<number | null>(null);
  const [showLoyaltySection, setShowLoyaltySection] = useState(false);
  const [forceManualLoyaltyInput, setForceManualLoyaltyInput] = useState(false);
  const [isManualOverrideEnabled, setIsManualOverrideEnabled] = useState(false);
  const [manualEntryMode, setManualEntryMode] = useState<'earned' | 'total'>('earned');
  const [loyaltyCalculationDescription, setLoyaltyCalculationDescription] = useState<string>('');
  const [cruiseAlreadyCompleted, setCruiseAlreadyCompleted] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm<PostCruiseFormData>();

  const watchGamblingWins = watch('gamblingWins');
  const watchGamblingLosses = watch('gamblingLosses');
  const watchLoyaltyOverride = watch('loyaltyPointsOverride');
  const watchCabinType = watch('cabinType');

  // Calculate gambling net in real-time
  const gamblingNet = (Number(watchGamblingWins) || 0) - (Number(watchGamblingLosses) || 0);

  // Safely parse loyalty override number (avoiding NaN on empty input)
  const parsedOverride = watchLoyaltyOverride !== undefined && watchLoyaltyOverride !== null && !isNaN(Number(watchLoyaltyOverride)) && String(watchLoyaltyOverride).trim() !== ''
    ? Number(watchLoyaltyOverride)
    : null;

  // Calculate final loyalty points (either projected or override, supporting 'earned' or 'total' mode)
  const finalLoyaltyPoints = isManualOverrideEnabled && parsedOverride !== null 
    ? (manualEntryMode === 'total' ? parsedOverride : currentLoyaltyPoints + parsedOverride)
    : projectedLoyaltyPoints !== null 
      ? currentLoyaltyPoints + projectedLoyaltyPoints 
      : currentLoyaltyPoints;

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500); // 500ms minimum skeleton display

    return () => clearTimeout(timer);
  }, []);

  // Track when data is actually loaded
  useEffect(() => {
    if (!loadingCruise && cruise) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [loadingCruise, cruise]);

  useEffect(() => {
    const fetchCruise = async () => {
      if (!id) return;
      
      try {
        setLoadingCruise(true);
        setDataLoaded(false);
        const cruiseData = await getCruise(id);
        if (cruiseData) {
          setCruise(cruiseData);
          setCruiseAlreadyCompleted(cruiseData.completed || false);
          setTargetLoyaltyProgram(cruiseData.loyaltyAccrualCruiseLine || cruiseData.cruiseLine);
          
          reset({
            onboardExpenses: cruiseData.onboardExpenses || undefined,
            gamblingWins: cruiseData.gamblingWins || undefined,
            gamblingLosses: cruiseData.gamblingLosses || undefined,
            notes: cruiseData.notes || '',
            ratings: cruiseData.ratings || {}
          });
        }
      } catch (error) {
        console.error('Error fetching cruise:', error);
      } finally {
        setLoadingCruise(false);
      }
    };

    fetchCruise();
  }, [id, getCruise, reset]);

  // Derive loyalty state whenever targetLoyaltyProgram changes
  useEffect(() => {
    if (!cruise || !targetLoyaltyProgram) return;

    const sailedRules = getLoyaltyCalculationRules(cruise.cruiseLine);
    if (sailedRules?.loyaltyCombinedGroup) {
      const groupLines = getCruiseLinesInLoyaltyGroup(sailedRules.loyaltyCombinedGroup);
      setAvailableLoyaltyPrograms(groupLines);
    } else {
      setAvailableLoyaltyPrograms([]);
    }

    const loyaltyRules = getLoyaltyCalculationRules(targetLoyaltyProgram);
    const loyaltyData = getLoyaltyTiersForCruiseLine(targetLoyaltyProgram);

    if (loyaltyData && loyaltyRules) {
      setShowLoyaltySection(true);
      setForceManualLoyaltyInput(loyaltyRules.forceManualInput || false);
      setLoyaltyCalculationDescription(getLoyaltyCalculationDescription(loyaltyRules));

      const currentStatus = loyaltyStatus.find(status => status.cruiseLineName === targetLoyaltyProgram);
      let currentPoints = currentStatus ? currentStatus.status : 0;
      if (
        isCarnivalCruiseLine(targetLoyaltyProgram) &&
        currentStatus?.grandfatheredTier === 'Platinum' &&
        (loyaltyData.unit?.toLowerCase().includes('star') || loyaltyRules.forceManualInput)
      ) {
        currentPoints = Math.max(CARNIVAL_PLATINUM_BOOST_STARS, currentPoints);
      }
      setCurrentLoyaltyPoints(currentPoints);

      if (!loyaltyRules.forceManualInput && !cruise.completed) {
        const effectiveCabinType = watchCabinType || cruise.cabinType || "";
        const tempCruise = { ...cruise, cabinType: effectiveCabinType } as Cruise;
        const calculatedPoints = calculateCruiseLoyaltyPoints(tempCruise, { loyaltyCalculationRules: loyaltyRules });
        setProjectedLoyaltyPoints(calculatedPoints);
      } else {
        setProjectedLoyaltyPoints(null);
        if (!cruise.completed) {
          setIsManualOverrideEnabled(true);
        }
      }
    } else {
      setShowLoyaltySection(false);
    }
  }, [cruise, targetLoyaltyProgram, loyaltyStatus, getLoyaltyCalculationRules, getLoyaltyTiersForCruiseLine, getCruiseLinesInLoyaltyGroup, watchCabinType]);

  const onSubmit = async (data: PostCruiseFormData) => {
    // If cruise is already completed, save directly without showing modal
    if (cruiseAlreadyCompleted) {
      await handleDirectSave(data);
    } else {
      // Store the form data and show confirmation modal for new completions
      setPendingFormData(data);
      setShowCompletionModal(true);
    }
  };

  const handleDirectSave = async (data: PostCruiseFormData) => {
    if (!id || !cruise) return;
    
    try {
      setIsLoading(true);
      
      const cruiseUpdates: Partial<Cruise> = {
        ...data,
        completed: cruise.completed // Preserve existing completion status
      };

      if (showLoyaltySection && cruise.cruiseLine && targetLoyaltyProgram !== cruise.cruiseLine) {
         cruiseUpdates.loyaltyAccrualCruiseLine = targetLoyaltyProgram;
      }

      await updateCruise(id, cruiseUpdates);

      navigate(`/cruise/${id}`, { replace: true });
    } catch (error) {
      console.error('Error updating cruise:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!id || !pendingFormData || !cruise) return;
    
    try {
      setIsLoading(true);
      
      // Determine points delta for cruise and new total points
      const enteredPoints = pendingFormData.loyaltyPointsOverride !== undefined ? Number(pendingFormData.loyaltyPointsOverride) : undefined;
      let finalPoints = currentLoyaltyPoints;
      let cruiseLoyaltyPointsOverride = enteredPoints;

      if (isManualOverrideEnabled && enteredPoints !== undefined) {
        if (manualEntryMode === 'total') {
          finalPoints = enteredPoints;
          cruiseLoyaltyPointsOverride = Math.max(0, enteredPoints - currentLoyaltyPoints);
        } else {
          finalPoints = currentLoyaltyPoints + enteredPoints;
          cruiseLoyaltyPointsOverride = enteredPoints;
        }
      } else if (projectedLoyaltyPoints !== null) {
        finalPoints = currentLoyaltyPoints + projectedLoyaltyPoints;
      }

      if (
        isCarnivalCruiseLine(targetLoyaltyProgram) &&
        targetUserStatus?.grandfatheredTier === 'Platinum' &&
        (loyaltyData?.unit?.toLowerCase().includes('star') || forceManualLoyaltyInput)
      ) {
        finalPoints = Math.max(CARNIVAL_PLATINUM_BOOST_STARS, finalPoints);
      }

      // Update cruise details
      const cruiseUpdates: Partial<Cruise> = {
        ...pendingFormData,
        loyaltyPointsOverride: isManualOverrideEnabled ? cruiseLoyaltyPointsOverride : (pendingFormData.loyaltyPointsOverride ?? projectedLoyaltyPoints ?? undefined),
        completed: true
      };

      if (showLoyaltySection && cruise.cruiseLine && targetLoyaltyProgram !== cruise.cruiseLine) {
         cruiseUpdates.loyaltyAccrualCruiseLine = targetLoyaltyProgram;
      }

      await updateCruise(id, cruiseUpdates);

      // Update loyalty status if applicable and cruise wasn't already completed
      if (showLoyaltySection && !cruiseAlreadyCompleted) {
        // Update loyalty status
        const updatedLoyaltyStatus = [...loyaltyStatus];
        const existingStatusIndex = updatedLoyaltyStatus.findIndex(
          status => status.cruiseLineName === targetLoyaltyProgram
        );

        if (existingStatusIndex >= 0) {
          updatedLoyaltyStatus[existingStatusIndex].status = finalPoints;
        } else {
          updatedLoyaltyStatus.push({
            cruiseLineName: targetLoyaltyProgram,
            status: finalPoints
          });
        }

        await updateUserLoyaltyStatus(updatedLoyaltyStatus);
      }

      navigate(`/cruise/${id}`, { replace: true });
    } catch (error) {
      console.error('Error updating cruise:', error);
    } finally {
      setIsLoading(false);
      setShowCompletionModal(false);
      setPendingFormData(null);
    }
  };

  const handleCancelSave = () => {
    setShowCompletionModal(false);
    setPendingFormData(null);
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return (
      <PostCruiseDetailsSkeleton />
    );
  }

  if (!cruise) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cruise Not Found</h2>
        <p className="text-gray-600 mb-4">The cruise you're trying to update doesn't exist or you don't have access to it.</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-accent"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  const targetUserStatus = loyaltyStatus.find(status => status.cruiseLineName === targetLoyaltyProgram);
  const loyaltyData = getLoyaltyTiersForCruiseLine(targetLoyaltyProgram);
  const capitalizedUnit = loyaltyData?.unit ? loyaltyData.unit.charAt(0).toUpperCase() + loyaltyData.unit.slice(1) : 'Points';
  const currentTierName = loyaltyData ? getLoyaltyTierName(targetLoyaltyProgram, currentLoyaltyPoints, targetUserStatus?.grandfatheredTier) : null;
  const finalTierName = loyaltyData ? getLoyaltyTierName(targetLoyaltyProgram, finalLoyaltyPoints, targetUserStatus?.grandfatheredTier) : null;

  return (
    <motion.div 
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Completion Confirmation Modal - Only show for new completions - Using Portal */}
      {showCompletionModal && pendingFormData && !cruiseAlreadyCompleted && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 text-blue-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-800">Save Cruise Details</h2>
              </div>
              <button
                onClick={handleCancelSave}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
            <p className="text-gray-600">
              Are you ready to save your cruise details and mark this cruise as completed?
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm">
                <p className="font-medium text-blue-900">{cruise.shipName}</p>
                <p className="text-blue-700">{cruise.cruiseLine}</p>
                <p className="text-blue-600">
                  {formatDate(parseISO(cruise.departureDate))} - {formatDate(parseISO(cruise.returnDate))}
                </p>
              </div>
            </div>

            {/* Show loyalty update info if applicable and not already completed */}
            {showLoyaltySection && !cruiseAlreadyCompleted && (
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Award className="h-4 w-4 text-secondary-600 mr-2" />
                  <h4 className="font-medium text-teal-900">Loyalty Status Update</h4>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-teal-700">Current {capitalizedUnit}:</span>
                    <span className="font-medium text-teal-800">{formatLoyaltyPoints(currentLoyaltyPoints)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-teal-700">New Total {capitalizedUnit}:</span>
                    <span className="font-medium text-teal-800">{formatLoyaltyPoints(finalLoyaltyPoints)}</span>
                  </div>
                  {currentTierName !== finalTierName && finalTierName && (
                    <div className="flex justify-between pt-1 border-t border-teal-200">
                      <span className="text-teal-700">New Tier:</span>
                      <span className="font-bold text-teal-900">{finalTierName}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-3 pt-4">
              <button
                onClick={handleConfirmSave}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Save Details & Mark Completed
                  </>
                )}
              </button>
              
              <button
                onClick={handleCancelSave}
                disabled={isLoading}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              >
                Cancel
              </button>
            </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <div className="mb-6 flex items-center">
        <button 
          onClick={() => navigate(-1)}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            {cruiseAlreadyCompleted ? 'Edit Cruise Details' : 'Add Cruise Details'}
          </h1>
          <p className="text-gray-600 mt-1">
            {cruiseAlreadyCompleted 
              ? 'Update your cruise experience details'
              : 'Complete your cruise experience with post-trip details'
            }
          </p>
        </div>
      </div>

      {/* Editor Callout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
      >
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-blue-800 font-medium mb-1">Need to modify other details?</p>
            <p className="text-blue-700 text-sm mb-3">
              This form is for adding post-cruise details like expenses and notes. To edit basic cruise information like dates, ship, or cabin details, use the full editor.
            </p>
            <Link
              to={`/edit-cruise/${id}`}
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <Edit className="h-4 w-4 mr-1" />
              Open Full Cruise Editor
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Cruise Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg shadow-md p-6 text-white mb-6"
      >
        <div className="flex items-center mb-4">
          <CustomCruiseIcon size={54} className="text-teal-400 mr-3" />
          <div>
            <h2 className="text-2xl font-semibold">{cruise.shipName}</h2>
            <p className="text-blue-200">{cruise.cruiseLine}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-blue-200">Departure</p>
            <p className="font-medium">{formatDate(parseISO(cruise.departureDate))}</p>
          </div>
          <div>
            <p className="text-blue-200">Return</p>
            <p className="font-medium">{formatDate(parseISO(cruise.returnDate))}</p>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-6">
          {/* Loyalty Program Section */}
          {showLoyaltySection && loyaltyData && (
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-center mb-4">
                <Award className="h-6 w-6 text-teal-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">
                  {loyaltyData.programName || `${targetLoyaltyProgram} Loyalty Program`}
                </h3>
              </div>

              {/* Points Choice Selection */}
              {availableLoyaltyPrograms.length > 1 && !cruiseAlreadyCompleted && (
                 <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                   <label htmlFor="targetLoyaltyProgram" className="block text-sm font-medium text-teal-900 mb-2">
                     Where would you like to apply your earned points?
                   </label>
                   <select
                     id="targetLoyaltyProgram"
                     value={targetLoyaltyProgram}
                     onChange={(e) => setTargetLoyaltyProgram(e.target.value)}
                     className="w-full px-3 py-2 bg-white border border-teal-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-gray-900"
                   >
                     {availableLoyaltyPrograms.map(program => (
                        <option key={program.name} value={program.name}>
                          {program.name} {program.loyaltyProgramName ? `(${program.loyaltyProgramName})` : ''}
                        </option>
                     ))}
                   </select>
                   <p className="text-xs text-teal-700 mt-2">
                     These cruise lines are part of the same loyalty group. You can direct your points to any of their programs.
                   </p>
                 </div>
              )}

              {cruiseAlreadyCompleted ? (
                // Show status for already completed cruise
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-green-900 mb-2">You have already added points for this sailing</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-green-700">Current Total:</span>
                          <span className="font-bold text-green-800">
                            {formatLoyaltyPoints(currentLoyaltyPoints)} {loyaltyData.unit}
                          </span>
                        </div>
                        {currentTierName && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-green-700">Current Tier:</span>
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-500 mr-1" />
                              <span className="font-medium text-green-800">{currentTierName}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 pt-3 border-t border-green-200">
                        <p className="text-sm text-green-700 mb-2">
                          Need to make further updates to your loyalty points?
                        </p>
                        <Link
                          to="/profile"
                          className="inline-flex items-center text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Edit in User Profile
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Show calculation/input for new completion
                <>
                  {/* Missing Room Type for Loyalty Calculation */}
                  {!cruise.cabinType && getLoyaltyCalculationRules(targetLoyaltyProgram)?.method === 'cabinTypeMultiplier' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <label htmlFor="cabinType" className="block text-sm font-medium text-amber-900 mb-2">
                        Room type is required to calculate your loyalty points
                      </label>
                      <select
                        id="cabinType"
                        {...register('cabinType', { required: 'Please select a room type for point calculation' })}
                        className={`w-full px-3 py-2 bg-white border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent text-sm text-gray-900 ${
                          errors.cabinType ? 'border-red-500 focus:ring-red-500' : 'border-amber-300 focus:ring-amber-500'
                        }`}
                      >
                        <option value="">Select your room type</option>
                        <option value="Interior">Interior</option>
                        <option value="Ocean View">Ocean View</option>
                        <option value="Balcony">Balcony</option>
                        <option value="Suite">Suite</option>
                      </select>
                      {errors.cabinType && (
                        <p className="mt-1 text-xs text-red-600">{errors.cabinType.message}</p>
                      )}
                      <p className="text-xs text-amber-700 mt-2">
                        This will be saved to your cruise details.
                      </p>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center mb-2">
                          <Star className="h-4 w-4 text-blue-600 mr-1" />
                          <span className="text-sm font-medium text-blue-900">Current Status</span>
                        </div>
                        <p className="text-lg font-bold text-blue-800">
                          {formatLoyaltyPoints(currentLoyaltyPoints)} {loyaltyData.unit}
                        </p>
                        {currentTierName && (
                          <p className="text-sm text-blue-700">Tier: {currentTierName}</p>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center mb-2">
                          <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-sm font-medium text-green-900">After This Cruise</span>
                        </div>
                        <p className="text-lg font-bold text-green-800">
                          {formatLoyaltyPoints(finalLoyaltyPoints)} {loyaltyData.unit}
                        </p>
                        {finalTierName && (
                          <p className="text-sm text-green-700">
                            Tier: {finalTierName}
                            {currentTierName !== finalTierName && (
                              <span className="ml-1 text-xs bg-green-200 text-green-800 px-1 rounded">NEW!</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Loyalty Points Calculation */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calculator className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">{capitalizedUnit} Calculation</span>
                      </div>
                      {!forceManualLoyaltyInput && (
                        <button
                          type="button"
                          onClick={() => setIsManualOverrideEnabled(!isManualOverrideEnabled)}
                          className="flex items-center text-sm text-teal-600 hover:text-teal-700"
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          {isManualOverrideEnabled ? 'Use Auto Calculation' : 'Manual Override'}
                        </button>
                      )}
                    </div>

                    {!isManualOverrideEnabled && projectedLoyaltyPoints !== null ? (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-600 mb-1">{loyaltyCalculationDescription}</p>
                        <p className="font-medium text-gray-800">
                          Earning {formatLoyaltyPoints(projectedLoyaltyPoints)} {loyaltyData.unit} for this cruise
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Segmented Mode Selector for Manual Entry */}
                        <div className="flex rounded-lg bg-gray-100 p-1 border border-gray-200">
                          <button
                            type="button"
                            onClick={() => setManualEntryMode('earned')}
                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-semibold transition-all ${
                              manualEntryMode === 'earned'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {`${capitalizedUnit} Earned This Cruise`}
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualEntryMode('total')}
                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-semibold transition-all ${
                              manualEntryMode === 'total'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Updated Account Balance Total
                          </button>
                        </div>

                        {/* Contextual Notice for Carnival Rewards */}
                        {targetLoyaltyProgram.toLowerCase().includes('carnival') && (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-900 space-y-1">
                            <div className="flex items-center gap-1.5 font-semibold text-indigo-950">
                              <Info className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                              <span>Carnival Rewards Earning Notice</span>
                            </div>
                            <p className="text-indigo-800">
                              Carnival Rewards earns 3 Stars per $1 spent on cruise fares & onboard expenditures (plus 1 Star per Casino Point).
                            </p>
                            {targetUserStatus?.grandfatheredTier && (
                              <div className="mt-1 pt-1.5 border-t border-indigo-200/60 flex items-center gap-1 text-indigo-900 font-medium">
                                <ShieldCheck className="h-3.5 w-3.5 text-indigo-600 flex-shrink-0" />
                                <span>
                                  {targetUserStatus.grandfatheredTier === 'Diamond'
                                    ? 'You hold permanent Lifetime Diamond status.'
                                    : targetUserStatus.grandfatheredTier === 'Platinum'
                                    ? 'Your Platinum status is protected through May 31, 2028 (+10,000 Star biennial bonus active).'
                                    : `Grandfathered ${targetUserStatus.grandfatheredTier} status active.`}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        <div>
                          <label htmlFor="loyaltyPointsOverride" className="block text-sm font-medium text-gray-700 mb-1.5">
                            {manualEntryMode === 'total' 
                              ? `Updated Total ${capitalizedUnit} Balance` 
                              : forceManualLoyaltyInput 
                              ? `${capitalizedUnit} Earned` 
                              : `Manual ${capitalizedUnit} Override`}
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              id="loyaltyPointsOverride"
                              {...register('loyaltyPointsOverride', {
                                required: forceManualLoyaltyInput ? `Please enter ${manualEntryMode === 'total' ? 'updated total balance' : `${capitalizedUnit.toLowerCase()} earned`}` : false,
                                min: { value: 0, message: 'Value must be 0 or greater' },
                                valueAsNumber: true
                              })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 bg-white"
                              placeholder={
                                manualEntryMode === 'total'
                                  ? `Enter new balance (e.g. ${currentLoyaltyPoints + 1000})`
                                  : `Enter ${capitalizedUnit.toLowerCase()} earned`
                              }
                            />
                            <span className="text-sm text-gray-500">{loyaltyData.unit || 'points'}</span>
                          </div>
                          {errors.loyaltyPointsOverride && (
                            <p className="mt-1 text-xs text-red-600">{errors.loyaltyPointsOverride.message}</p>
                          )}
                          <p className="mt-1.5 text-xs text-gray-500">
                            {manualEntryMode === 'total'
                              ? `Entering your new total balance will automatically calculate the ${loyaltyData.unit || 'points'} gained from this sailing.`
                              : `Enter the amount earned from this sailing to add to your current balance (${formatLoyaltyPoints(currentLoyaltyPoints)} ${loyaltyData.unit || 'points'}).`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Onboard Expenses */}
          <div>
            <label htmlFor="onboardExpenses" className="block text-lg font-semibold text-gray-800 mb-2">
              Onboard Expenses
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Total amount spent on the ship for spa treatments, specialty dining, drinks, shopping, excursions, etc.
            </p>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CurrencyIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                step="0.01"
                id="onboardExpenses"
                {...register('onboardExpenses')}
                className="pl-10 w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 text-lg"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Gambling Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Casino Activity</h3>
            <p className="text-sm text-gray-600 mb-4">
              Track your casino wins and losses during the cruise.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="gamblingWins" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center">
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                    Gambling Wins
                  </div>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    id="gamblingWins"
                    {...register('gamblingWins')}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="gamblingLosses" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center">
                    <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                    Gambling Losses
                  </div>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    id="gamblingLosses"
                    {...register('gamblingLosses')}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Gambling Net Display */}
            {(watchGamblingWins || watchGamblingLosses) && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Net Gambling Result:</span>
                  <span className={`text-lg font-bold ${gamblingNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {gamblingNet >= 0 ? '+' : ''}{formatCurrency(gamblingNet)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Cruise Ratings Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center mb-4">
              <Star className="h-6 w-6 text-yellow-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Cruise Ratings</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Rate your cruise experience on a scale of 1 to 10. Click on the stars to select your rating.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StarRating
                value={watch('ratings.shipExperience') || 0}
                onChange={(value) => setValue('ratings.shipExperience', value, { shouldValidate: true })}
                label="Ship Experience"
              />
              <StarRating
                value={watch('ratings.service') || 0}
                onChange={(value) => setValue('ratings.service', value, { shouldValidate: true })}
                label="Service"
              />
              <StarRating
                value={watch('ratings.stateroom') || 0}
                onChange={(value) => setValue('ratings.stateroom', value, { shouldValidate: true })}
                label="Stateroom"
              />
              <StarRating
                value={watch('ratings.foodDining') || 0}
                onChange={(value) => setValue('ratings.foodDining', value, { shouldValidate: true })}
                label="Food & Dining"
              />
              <StarRating
                value={watch('ratings.barBeverages') || 0}
                onChange={(value) => setValue('ratings.barBeverages', value, { shouldValidate: true })}
                label="Bar & Beverages"
              />
              <StarRating
                value={watch('ratings.entertainment') || 0}
                onChange={(value) => setValue('ratings.entertainment', value, { shouldValidate: true })}
                label="Entertainment"
              />
            </div>
          </div>

          {/* Notes Section */}
          <div className="border-t border-gray-200 pt-6">
            <label htmlFor="notes" className="block text-lg font-semibold text-gray-800 mb-2">
              <div className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Cruise Notes
              </div>
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Add any additional thoughts, memories, or details about your cruise experience.
            </p>
            <textarea
              id="notes"
              {...register('notes')}
              rows={4}
              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              placeholder="Share your cruise highlights, favorite moments, recommendations for future cruisers..."
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/cruise/${id}`)}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center px-6 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <CheckCircle className="h-5 w-5 mr-2" />
            )}
            {isLoading ? 'Saving...' : cruiseAlreadyCompleted ? 'Save Changes' : 'Save Details'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default PostCruiseDetails;
